// TES-1316 (S3 of TES-1311) — the care meeting's guided documentation, A-02
// "KANONISKT TILLÄGG 2026-10-13 — GUIDAT DOKUMENTATIONSFLÖDE EFTER FÖRSTA
// VÅRDMÖTET":
//   "Flödet ska vara stegindelat och progressivt. Läkaren ska bara se de
//   uppgifter och bekräftelser som är relevanta för det valda utfallet."
// TES-1405 — Emelie's later answer to Ö-456 (TES-1396; the latest dated
// decision wins) splits A-02's five steps into six:
//   "1. mötesläge och dokumentation, 2. sammanställ journal, 3. medicinskt
//   utfall, 4. recept och patienttexter, 5. Webdoc-bekräftelser,
//   6. förhandsvisa och publicera."
// Step 1 is meeting mode; steps 2–6 replace the documentation column, never
// the video. Nothing is published, and the meeting is not documented,
// before step 6's publish.
//
// Pure on purpose: no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.

export type MeetingStep = 1 | 2 | 3 | 4 | 5 | 6;

// Ö-456's step names, verbatim with a capital first letter.
export const MEETING_STEP_TITLE: Record<MeetingStep, string> = {
  1: "Mötesläge och dokumentation",
  2: "Sammanställ journal",
  3: "Medicinskt utfall",
  4: "Recept och patienttexter",
  5: "Webdoc-bekräftelser",
  6: "Förhandsvisa och publicera",
};

// A-02: "Ett avslutat steg ska följas av en avgränsad nästa vy med kort
// instruktion". No document gives the instructions verbatim; these are
// ours and listed in the PR.
export const MEETING_STEP_INSTRUCTION: Record<MeetingStep, string> = {
  1: "Fyll i journalfälten under samtalet. Sammanställ journalanteckningen när samtalet är klart.",
  2: "Granska den sammanställda journalanteckningen. Utfall och recept förs in i den när du har valt dem.",
  3: "Välj utfallet av vårdmötet. Det avgör vad du fyller i i nästa steg.",
  4: "Fyll i det som hör till det valda utfallet och granska journalanteckningen.",
  5: "För in journalanteckningen i Webdoc och bekräfta de åtgärder du har gjort där.",
  6: "Kontrollera det som publiceras till patienten. Inget publiceras förrän du bekräftar.",
};

/**
 * The steps a meeting walks. TES-1228 — a voluntary meeting has no outcome,
 * no plan and nothing published to her: step 1, then the journal tick in
 * step 5, which documents it.
 */
export function meetingSteps(voluntary: boolean): MeetingStep[] {
  return voluntary ? [1, 5] : [1, 2, 3, 4, 5, 6];
}

/** The step after `step`, or null when `step` is the last one. */
export function nextMeetingStep(
  step: MeetingStep,
  voluntary: boolean,
): MeetingStep | null {
  const steps = meetingSteps(voluntary);
  return steps[steps.indexOf(step) + 1] ?? null;
}

/** The step before `step`, or null in meeting mode. */
export function previousMeetingStep(
  step: MeetingStep,
  voluntary: boolean,
): MeetingStep | null {
  const steps = meetingSteps(voluntary);
  const i = steps.indexOf(step);
  return i > 0 ? steps[i - 1]! : null;
}

export interface WebdocTicks {
  /** The direct approval's e-recept tick is shown (TES-1315). */
  eRecept: boolean;
  eReceptDone: boolean;
  /** The remiss tick is shown (a new blood test was asked for). */
  labOrder: boolean;
  labOrderDone: boolean;
  journalDone: boolean;
}

/**
 * What keeps step 5 from going on, or null. The e-recept comes first, since
 * the journal tick waits for it (TES-1315); the journal tick is always
 * required ("alltid innan publicering", A-02 STEG 4, Ö-456's step 5).
 */
export function webdocTicksProblem(t: WebdocTicks): string | null {
  if (t.eRecept && !t.eReceptDone)
    return "Bekräfta att e-receptet är utfärdat i Webdoc.";
  if (t.labOrder && !t.labOrderDone)
    return "Bekräfta att provbeställning/remiss för blodprov är skapad i Webdoc.";
  if (!t.journalDone)
    return "Bekräfta att journalanteckningen är införd i Webdoc.";
  return null;
}
